import React, { useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Box, Skeleton, Typography, useTheme } from "@mui/material";
import InfoToltip from "../components/InfoToltip";
import { NO_DATA_AVAILABLE } from "../../../utils/constant";

type ChartData = {
    categories: string[];
    values: number[];
    bookPercentage: number[];
    ids?: (string | number)[];
};

interface Props {
    title: string;
    data: ChartData;

    // state control
    isLoading?: boolean;
    isError?: boolean;
    isEmpty?: boolean;

    // optional UI
    rightComponent?: React.ReactNode;
    tooltipDescription?: string;

    // formatting (parent controlled)
    formatValue?: (val: number) => string;
    formatPercent?: (val: number) => string;

    // tooltip data
    totalValue?: number;
}

export default function CustomBarChart({
    title,
    data,
    isLoading = false,
    isError = false,
    isEmpty = false,
    rightComponent,
    tooltipDescription,
    formatValue,
    formatPercent,
    totalValue = 0,
}: Props): JSX.Element {
    const theme = useTheme();

    const chartOptions = useMemo(() => {
        return {
            chart: {
                type: "bar",
                style: {
                    fontFamily: theme.typography.fontFamily,
                },
            },

            title: { text: "" },

            xAxis: {
                categories: data.categories,
                gridLineWidth: 1,
                lineWidth: 0,
                gridLineColor: "#D9E3FF80",
                gridLineDashStyle: "dash",
                labels: {
                    align: "left",
                    reserveSpace: true,
                    useHTML: true,
                    formatter: function (this: any) {
                        const text = String(this?.value ?? "")?.toLowerCase();
                        const id = data?.ids?.[this?.pos];
                        const titleText = id
                            ? `${String(text).toUpperCase()} (${id})`
                            : String(text).toUpperCase();

                        return `<div 
              title="${titleText}" 
              style="
                text-overflow: ellipsis;
                overflow: hidden;
                white-space: nowrap;
                max-width: 150px;
                display: block;
              "
            >${text}</div>`;
                    },
                    style: {
                        fontSize: "15px",
                        textTransform: "capitalize",
                    },
                },
            },

            yAxis: {
                min: 0,
                title: { text: "" },
                gridLineWidth: 0,
                labels: {
                    rotation: -45,
                    formatter: function (this: any) {
                        return formatValue ? formatValue(this?.value) : this?.value;
                    },
                    style: {
                        fontSize: "14px",
                        whiteSpace: "nowrap",
                    },
                },
            },

            tooltip: {
                useHTML: true,
                formatter: function (this: any) {
                    const value = this?.y;
                    const bookPercentage = this?.point?.custom?.percent;
                    return `
                    <div 
                        style="
                        display: flex;
                        justify-content: center;
                        flex-direction: column;
                        width: 200px;
                        height: 60px;
                        background-color: ${theme.palette.customColors.cinder}; 
                        border-radius: 0px; 
                        border-width: 1px;
                        padding: 10px;
                        "
                    >
                        <div style="display: flex; justify-content: space-between;">
                        <p style="font-size: 14px;color: #FFFFFF;">Market Value</p>
                        <p style="font-size: 14px;color: #FFFFFF;">% of book</p>
                        </div>

                        <div style="display: flex; justify-content: space-between;margin-top:-20px">
                        <p style="color: #FFFFFF;font-size: 16px;">
                            ${formatValue ? formatValue(value) : value}
                        </p>

                        <p style="color: #FFFFFF;font-size: 16px;">
                            ${formatPercent
                            ? formatPercent(bookPercentage)
                            : bookPercentage
                        }
                        </p>
                        </div>
                    </div>
                    `;
                },
                backgroundColor: theme.palette.common.black,
                opacity: 0.1,
                borderWidth: 0,
                borderRadius: 20,
                shadow: false,
                shape: "square",
                followPointer: true,
            },

            plotOptions: {
                bar: {
                    borderRadius: 8,
                    pointWidth: 10,
                    groupPadding: 0.1,
                },
            },

            series: [
                {
                    data: data?.values?.map((val, index) => ({
                        y: val ?? "-",
                        name: data?.categories?.[index],
                        custom: {
                            percent: data?.bookPercentage ? data?.bookPercentage?.[index] + '%' : '-',
                        },
                    })),
                    color: theme.palette.primary[500],
                    showInLegend: false,
                },
            ],

            credits: { enabled: false },
        };
    }, [data, theme, formatValue, formatPercent, totalValue]);

    // ---------------- UI STATES ----------------

    if (isLoading) return <ChartSkeleton />;

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                padding: "25px 30px 45px 30px",
            }}
        >
            {/* HEADER (same as old UI) */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    paddingBottom: "40px",
                    alignItems: { xs: "baseline" },
                }}
            >
                <Typography variant="h2">
                    {title}
                    {tooltipDescription && (
                        <InfoToltip desc={tooltipDescription} />
                    )}
                </Typography>

                {rightComponent}
            </Box>

            {/* CHART AREA */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                }}
            >
                {isError ? (
                    <StateWrapper text="Something went wrong" />
                ) : isEmpty || !data?.values?.length ? (
                    <StateWrapper text={NO_DATA_AVAILABLE} />
                ) : (
                    <HighchartsReact
                        highcharts={Highcharts}
                        options={chartOptions}
                        containerProps={{
                            style: {
                                height: 350,
                                width: "100%",
                            },
                        }}
                    />
                )}
            </Box>
        </Box>
    );
}

// ---------------- STATES ----------------

const StateWrapper = ({ text }: { text: string }) => (
    <Box
        sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
        }}
    >
        <Typography variant="h2" textAlign="center">
            {text}
        </Typography>
    </Box>
);

const ChartSkeleton = () => (
    <Box
        sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            padding: "2rem",
        }}
    >
        {/* HEADER SPACE (title + dropdown) */}
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 4,
            }}
        >
            <Skeleton width={180} height={30} />
            <Skeleton width={120} height={35} />
        </Box>

        {/* CHART AREA */}
        <Box
            sx={{
                flex: 1,
                display: "flex",
                flexDirection: "row",
                gap: 2,
            }}
        >
            {/* Y AXIS LABELS */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {[...Array(6)].map((_, i) => (
                    <Skeleton key={i} width={100} height={20} />
                ))}
            </Box>

            {/* BARS */}
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 3 }}>
                {[80, 60, 90, 50, 70, 40].map((w, i) => (
                    <Skeleton key={i} width={`${w}%`} height={20} />
                ))}
            </Box>
        </Box>

        {/* X AXIS LABELS */}
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: 2,
                pl: "100px",
                pr: 2,
            }}
        >
            {[...Array(5)].map((_, i) => (
                <Skeleton key={i} width={20} height={14} />
            ))}
        </Box>
    </Box>
);

